export type TFunction = (...args: any[]) => any;

/** Format a string to be used as an id. */
export function formatForId (text: string): string {
    return stripTags(text.toLocaleLowerCase().replace(' ', '-'));
}

type TPosition = {
    x: number;
    y: number;
};

/** Get the position of an element in the page. */
export function getElementPosition (element: HTMLElement): TPosition {
    const horizontalPosition = element.getBoundingClientRect().left + window.scrollX;
    const verticalPosition = element.getBoundingClientRect().top + window.scrollY;

    return {
        x: Math.round(horizontalPosition),
        y: Math.round(verticalPosition)
    };
}

/** Remove all tags from a string. */
export function stripTags (text: string): string {
    return text.replace(/(<([^>]+)>)/gi, '');
}

/** Disable scroll on the body. */
export function disableBodyScroll (): void {
    document.body.style.overflow = 'hidden';
}

/** Enable scroll on the body. */
export function enableBodyScroll (): void {
    document.body.style.overflow = 'initial';
}

/** Close all menus */
export function closeAllMenus (): void {
    // We dispatch our custom events to close the menus
    document.dispatchEvent(new Event('closeHeaderMenu'));
    document.dispatchEvent(new Event('closeHeaderShortcutsMenu'));
}

/** Set a cookie. */
export function setCookie (key: string, value: string, days: number): void {
    // We calculate the max age in seconds
    const maxAge = 60 * 60 * 24 * days;

    // We set the cookie
    document.cookie = `${key}=${value}; max-age=${maxAge}; path=/;`;
}

/** Get a cookie. */
export function getCookie (key: string): string {
    // Get all cookies in an array
    const cookies = document.cookie.split("; ");

    // Find cookie using key
    const cookie = cookies.find((cookie) => cookie.startsWith(`${key}=`));

    if (cookie) {
        // Return cookie value
        return cookie.split('=')[1];
    } else {
        return null;
    }
}

/** Trap focus inside a container */
export function trapFocus (containerElement: HTMLElement): void {
    // Selector to match all focusable elements
    const selector = `
        a[href],
        area[href],
        input:not([disabled]),
        select:not([disabled]),
        textarea:not([disabled]),
        button:not([disabled]),
        iframe,
        object,
        embed,
        *[tabindex],
        *[contenteditable]
    `;

    // Get all focusable elements
    const focusableElements = containerElement.querySelectorAll(selector) as NodeListOf<HTMLElement>;

    // If there are no focusable elements we stop here
    if (focusableElements.length === 0) {
        return;
    }

    // Get first and last focusable elements
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Add a keydown event listener on the first element
    firstElement.addEventListener('keydown', (event) => {
        // If the tab and shift keys are down while the first element is focused
        if (
            event.key === 'Tab'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
            && event.shiftKey
        ) {
            // Stop the default behavior
            event.preventDefault();
            // Set the focus on the last element
            lastElement.focus();
        }
    });

    // Add a keydown event listener on the last element
    lastElement.addEventListener('keydown', (event) => {
        // If the tab key is down while the last element is focused
        if (
            event.key === 'Tab'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
            && !event.shiftKey
        ) {
            // Stop the default behavior
            event.preventDefault();
            // Set the focus on the first element
            firstElement.focus();
        }
    });
}